/**
 * Utility functions for condition/filter builders
 * Provides operators and field type detection logic
 */

/** Data types that support numeric comparison operators (aligned with report/list numeric columns). */
export const NUMERIC_DATA_TYPES = [
  'number',
  'integer',
  'decimal',
  'currency',
  'percent',
  'percentage',
  'double',
  'float',
  'int',
  'numeric',
  'real',
  'bigint',
  'smallint',
  'money',
];

export const isAutonumberDataType = (dataType) =>
  String(dataType || '').toLowerCase() === 'autonumber';

export const isNumericDataType = (dataType) => {
  const dt = String(dataType || '').toLowerCase();
  return isAutonumberDataType(dt) || NUMERIC_DATA_TYPES.includes(dt);
};

const NUMERIC_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'greater_than_or_equal', label: 'Greater Than Or Equal' },
  { value: 'less_than_or_equal', label: 'Less Than Or Equal' },
  { value: 'between', label: 'Between' },
  { value: 'is_null', label: 'Is Null' },
  { value: 'is_not_null', label: 'Is Not Null' },
];

const DATE_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'greater_than_or_equal', label: 'Greater Than Or Equal' },
  { value: 'less_than_or_equal', label: 'Less Than Or Equal' },
  { value: 'between', label: 'Between' },
  { value: 'is_null', label: 'Is Null' },
  { value: 'is_not_null', label: 'Is Not Null' },
];

function parseDisplayConfig(displayConfig) {
  if (!displayConfig) return {};
  if (typeof displayConfig === 'string') {
    try {
      return JSON.parse(displayConfig);
    } catch {
      return {};
    }
  }
  return displayConfig || {};
}

/** Rollup summary fields store their filter/display semantics in display_config.rollupSummary.outputType. */
export const getRollupSummaryOutputType = (field) => {
  const parsed = parseDisplayConfig(field?.display_config || field?.displayConfig);
  return String(parsed.rollupSummary?.outputType || 'number').toLowerCase();
};

/** Lowercase snake_case operator key with optional `old_` prefix stripped (object validation). */
const operatorKeyStrippingOldPrefix = (opValue) => {
  let n = String(opValue || '').toLowerCase().replace(/\s+/g, '_');
  if (n.startsWith('old_')) n = n.slice(4);
  return n;
};

/**
 * Get available operators based on field data type
 * @param {Object} field - Field object with data_type property
 * @param {Object} options - Options for operator customization
 * @returns {Array} Array of operator objects with {value, label}
 */
export const getOperatorsForField = (field, options = {}) => {
  if (!field) {
    // Default operators when no field is selected
    return [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not Equals' },
      { value: 'is_null', label: 'Is Null' },
      { value: 'is_not_null', label: 'Is Not Null' }
    ];
  }

  const normalizedType = getFieldDataType(field);

  // Check if it's a picklist field
  const isPicklist = normalizedType === 'picklist' || normalizedType === 'multipicklist';

  // Check if it's a boolean/checkbox field
  const isBoolean = normalizedType === 'boolean' || normalizedType === 'checkbox';

  const isNumeric = isNumericDataType(normalizedType);

  // Check if it's a text field (text, string, email, phone, url, textarea)
  const isText =
    normalizedType === 'text' ||
    normalizedType === 'string' ||
    normalizedType === 'email' ||
    normalizedType === 'phone' ||
    normalizedType === 'url' ||
    normalizedType === 'textarea';

  // If picklist operators are specified, use them
  if (isPicklist && options.picklistOperators) {
    return options.picklistOperators.map(op => {
      if (typeof op === 'string') {
        return { value: op, label: op };
      }
      return op;
    });
  }

  // If custom operators are specified, filter them based on field type
  if (options.customOperators) {
    let operators = options.customOperators.map(op => {
      if (typeof op === 'string') {
        return { value: op, label: op };
      }
      return op;
    });

    // For boolean fields, only allow = and != operators
    if (isBoolean) {
      const allowedBoolean = new Set([
        '=', '!=', '==', 'equals', 'not_equals',
        'is_null', 'is_not_null',
      ]);
      operators = operators.filter(op => {
        const opValue = typeof op === 'string' ? op : op.value;
        const baseKey = operatorKeyStrippingOldPrefix(opValue);
        return allowedBoolean.has(opValue) || allowedBoolean.has(baseKey);
      });
    }

    // For numeric fields, exclude IN, NOT IN, LIKE, and text-specific operators
    if (isNumeric) {
      operators = operators.filter(op => {
        const opValue = typeof op === 'string' ? op : op.value;
        const normalizedOp = operatorKeyStrippingOldPrefix(opValue);
        // Exclude IN, NOT IN, LIKE, and text-specific operators for numeric fields
        return normalizedOp !== 'in' && normalizedOp !== 'not_in' &&
               normalizedOp !== 'like' && normalizedOp !== 'contains' &&
               normalizedOp !== 'starts_with' && normalizedOp !== 'ends_with' &&
               opValue !== 'IN' && opValue !== 'NOT IN' && opValue !== 'LIKE' &&
               opValue !== 'Contains' && opValue !== 'Starts With' && opValue !== 'Ends With';
      });
    }

    // For text fields, exclude comparison operators (<, >, <=, >=) and LIKE
    if (isText) {
      operators = operators.filter(op => {
        const opValue = typeof op === 'string' ? op : op.value;
        const normalizedOp = operatorKeyStrippingOldPrefix(opValue);
        // Exclude comparison operators and LIKE for text fields
        return normalizedOp !== '<' && normalizedOp !== '>' &&
               normalizedOp !== '<=' && normalizedOp !== '>=' &&
               normalizedOp !== 'like' &&
               normalizedOp !== 'less_than' && normalizedOp !== 'greater_than' &&
               normalizedOp !== 'less_than_or_equal' && normalizedOp !== 'greater_than_or_equal' &&
               opValue !== 'LIKE';
      });
    }

    return operators;
  }

  if (
    normalizedType === 'date' ||
    normalizedType === 'datetime' ||
    normalizedType === 'time'
  ) {
    return DATE_OPERATORS;
  }

  if (isNumeric) {
    return NUMERIC_OPERATORS;
  }

  switch (normalizedType) {
    case 'boolean':
    case 'checkbox':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];

    case 'picklist':
    case 'multipicklist':
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'in', label: 'IN' },
        { value: 'not_in', label: 'NOT IN' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];

    default:
      // Default for text, email, phone, etc.
      return [
        { value: 'equals', label: 'Equals' },
        { value: 'not_equals', label: 'Not Equals' },
        { value: 'contains', label: 'Contains' },
        { value: 'not_contains', label: 'Not Contains' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'like', label: 'LIKE' },
        { value: 'is_null', label: 'Is Null' },
        { value: 'is_not_null', label: 'Is Not Null' }
      ];
  }
};

/**
 * Check if operator requires a value
 * @param {string} operator - Operator value
 * @returns {boolean} True if value is required
 */
const normalizeOpKey = (op) => (op == null || op === '' ? '' : String(op).toLowerCase().replace(/\s+/g, '_'));

export const isOperatorValueRequired = (operator) => {
  const n = normalizeOpKey(operator);
  const noValue = new Set([
    'is_null', 'is_not_null', 'isnull', 'isnotnull',
    'is_null_or_empty', 'is_not_null_or_empty', 'is_empty', 'is_not_empty',
    'ischanged', 'is_changed',
    'old_is_null', 'old_is_not_null',
  ]);
  return !noValue.has(n);
};

export const isBetweenOperator = (operator) => {
  const n = normalizeOpKey(operator);
  return n === 'between' || n === 'not_between';
};

/** Parse stored filter value for between / not_between (`from|to`). */
export const parseBetweenFilterValue = (value) => {
  if (value == null) return { from: '', to: '' };
  const s = String(value).trim();
  if (!s) return { from: '', to: '' };
  const parts = s.split('|').map((p) => p.trim());
  return { from: parts[0] || '', to: parts[1] || '' };
};

export const encodeBetweenFilterValue = (from, to) => {
  const a = String(from ?? '').trim();
  const b = String(to ?? '').trim();
  if (!a && !b) return '';
  return `${a}|${b}`;
};

export const isBetweenValueComplete = (value) => {
  const { from, to } = parseBetweenFilterValue(value);
  return from !== '' && to !== '';
};

/**
 * Check if field is a picklist type
 * @param {Object} field - Field object
 * @returns {boolean} True if field is picklist or multipicklist
 */
export const isPicklistField = (field) => {
  if (!field) return false;
  const dataType = (field.data_type || field.dataType || '').toLowerCase();
  return dataType === 'picklist' || dataType === 'multipicklist';
};

/**
 * Check if field is a boolean/checkbox type
 * @param {Object} field - Field object
 * @returns {boolean} True if field is boolean or checkbox
 */
export const isBooleanField = (field) => {
  if (!field) return false;
  const dataType = (field.data_type || field.dataType || '').toLowerCase();
  return dataType === 'boolean' || dataType === 'checkbox';
};

/**
 * Check if field supports multiple values (multipicklist with IN/NOT IN)
 * @param {Object} field - Field object
 * @param {string} operator - Operator value
 * @returns {boolean} True if multiple values are allowed
 */
export const isMultipleValuesAllowed = (field, operator) => {
  if (!isPicklistField(field)) return false;
  if (!operator) return false;
  // Normalize operator for comparison (case-insensitive, handle spaces)
  const normalizedOp = operator.toLowerCase().replace(/\s+/g, '_');
  const multiValueOperators = ['in', 'not_in'];
  return multiValueOperators.includes(normalizedOp);
};

/**
 * Get field by name from fields array
 * @param {Array} fields - Array of field objects
 * @param {string} fieldName - Field name to find
 * @param {Object} options - Options for field matching
 * @returns {Object|null} Field object or null
 */
export const getFieldByName = (fields, fieldName, options = {}) => {
  if (!fieldName || !Array.isArray(fields)) return null;

  const nameKey = options.nameKey || 'field_name';
  const altNameKey = options.altNameKey || 'name';

  const needle = String(fieldName);
  return (
    fields.find(
      field =>
        (field[nameKey] != null && String(field[nameKey]) === needle) ||
        (field[altNameKey] != null && String(field[altNameKey]) === needle)
    ) || null
  );
};

/**
 * Get field data type
 * @param {Object} field - Field object
 * @returns {string} Data type (lowercase)
 */
export const getFieldDataType = (field) => {
  if (!field) return 'text';
  const baseType = (
    field.data_type ||
    field.dataType ||
    field.type ||
    field.field_type ||
    'text'
  ).toLowerCase();

  if (baseType === 'rollupsummary') {
    return getRollupSummaryOutputType(field);
  }

  return baseType;
};

/**
 * Trim filter values before save/API use. Handles pipe-separated ranges (between).
 */
export const normalizeFilterValue = (value) => {
  if (value == null) return value;
  if (typeof value === 'string') {
    const s = value.trim();
    if (s.includes('|')) {
      return s.split('|').map((p) => p.trim()).join('|');
    }
    return s;
  }
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? v.trim() : v));
  }
  return value;
};

/**
 * Normalize a filter/condition row (field, operator, value) before persisting or querying.
 */
export const normalizeFilterRow = (filter) => {
  if (!filter || typeof filter !== 'object') return filter;
  const out = { ...filter };
  const trimStr = (v) => (typeof v === 'string' ? v.trim() : v);
  if ('field' in out) out.field = trimStr(out.field);
  if ('fieldName' in out) out.fieldName = trimStr(out.fieldName);
  if ('field_name' in out) out.field_name = trimStr(out.field_name);
  if ('operator' in out) out.operator = trimStr(out.operator);
  if ('value' in out) {
    out.value = normalizeFilterValue(out.value);
    const vt = out.valueType ?? out.value_type;
    const isField =
      vt === 'field' ||
      (typeof out.value === 'string' && /\{\{/.test(out.value));
    const isLiteral = vt === 'literal' || vt === 'date_literal';
    if (
      isField &&
      !isLiteral &&
      typeof out.value === 'string' &&
      out.value.trim() &&
      !/\{\{/.test(out.value)
    ) {
      const inner = out.value.trim();
      out.value = `{{${inner}}}`;
    }
  }
  if (out.valueType == null && out.value_type != null) {
    const vt = out.value_type;
    if (vt === 'field') out.valueType = 'field';
    else if (vt === 'literal' || vt === 'date_literal') out.valueType = 'literal';
    else out.valueType = 'static';
  }
  if (out.valueType == null) {
    out.valueType = 'static';
  }
  delete out.value_type;
  if ('displayValue' in out && typeof out.displayValue === 'string') {
    out.displayValue = out.displayValue.trim();
  }
  return out;
};

const getFilterRowFieldName = (filter) =>
  String(filter?.field ?? filter?.fieldName ?? '').trim();

const getFilterRowOperator = (filter) => String(filter?.operator ?? '').trim();

const hasFilterRowValue = (filter) => {
  const v = filter?.value;
  if (v == null) return false;
  if (Array.isArray(v)) {
    return v.some((item) => String(item ?? '').trim() !== '');
  }
  return String(v).trim() !== '';
};

/** True when the user has started editing a filter row (non-empty draft). */
export const isFilterRowStarted = (filter) => {
  if (!filter) return false;
  return (
    !!getFilterRowFieldName(filter) ||
    !!getFilterRowOperator(filter) ||
    hasFilterRowValue(filter)
  );
};

/** True when a list-view filter row is ready to persist (field + operator required; value optional for is_null, etc.). */
export const isCompleteListViewFilter = (filter) => {
  if (!filter) return false;

  const field = getFilterRowFieldName(filter);
  const operator = getFilterRowOperator(filter);
  if (!field || !operator) return false;

  if (!isOperatorValueRequired(operator)) return true;

  if (isBetweenOperator(operator)) {
    return isBetweenValueComplete(filter.value);
  }

  const valueType = filter.valueType ?? filter.value_type ?? 'static';
  if (valueType === 'field') {
    return hasFilterRowValue(filter);
  }

  return hasFilterRowValue(filter);
};

/** Validation errors for list-view / enhanced list-view filter rows. */
export const validateListViewFilters = (filters = []) => {
  const errors = [];

  filters.forEach((filter, index) => {
    if (!isFilterRowStarted(filter)) return;
    if (isCompleteListViewFilter(filter)) return;

    const field = getFilterRowFieldName(filter);
    const operator = getFilterRowOperator(filter);
    const label = `Filter ${index + 1}`;

    if (!field) {
      errors.push(`${label}: Field must be selected`);
      return;
    }
    if (!operator) {
      errors.push(`${label}: Operator must be selected`);
      return;
    }
    if (isBetweenOperator(operator)) {
      errors.push(`${label}: Enter both From and To values`);
      return;
    }

    const valueType = filter.valueType ?? filter.value_type ?? 'static';
    if (valueType === 'field') {
      errors.push(`${label}: Compare field must be selected`);
      return;
    }

    errors.push(`${label}: Value is required`);
  });

  return errors;
};

export const getCompleteListViewFilters = (filters = []) =>
  filters.filter(isCompleteListViewFilter).map(normalizeFilterRow);
